import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, Loader2, Send } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { PRACTICE_CASE_TYPES } from "@/constants/practice";

const DemandLetterPage = () => {
  const [form, setForm] = useState({
    case_type: 'personal_injury',
    jurisdiction: 'us_federal',
    sender_info: { name: '', firm: '', address: '' },
    recipient_info: { name: '', company: '', address: '' },
    facts: '',
    legal_basis: [],
    damages: {},
    demand_amount: '',
    deadline_days: 30,
    tone: 'firm',
    include_litigation_threat: true
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const generate = async () => {
    setLoading(true);
    try {
      const payload = { ...form, demand_amount: form.demand_amount ? parseFloat(form.demand_amount) : null };
      const res = await axios.post(`${API}/practice/demand-letter`, payload);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="demand-letter-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Demand Letters</h1>
          <p className="text-muted-foreground">Generate professional demand letters that get results</p>
        </div>
        
        {!result ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader><CardTitle>Letter Details</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Case Type</Label>
                      <Select value={form.case_type} onValueChange={v => setForm(f => ({...f, case_type: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{PRACTICE_CASE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Tone</Label>
                      <Select value={form.tone} onValueChange={v => setForm(f => ({...f, tone: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="firm">Firm</SelectItem>
                          <SelectItem value="aggressive">Aggressive</SelectItem>
                          <SelectItem value="conciliatory">Conciliatory</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Facts</Label>
                    <Textarea placeholder="Describe the facts giving rise to the claim..." value={form.facts} onChange={e => setForm(f => ({...f, facts: e.target.value}))} rows={4} />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Demand Amount ($)</Label>
                      <Input type="number" placeholder="50000" value={form.demand_amount} onChange={e => setForm(f => ({...f, demand_amount: e.target.value}))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Response Deadline (days)</Label>
                      <Input type="number" value={form.deadline_days} onChange={e => setForm(f => ({...f, deadline_days: parseInt(e.target.value) || 30}))} />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.include_litigation_threat} onChange={e => setForm(f => ({...f, include_litigation_threat: e.target.checked}))} className="rounded" />
                    <span className="text-sm">Include litigation threat</span>
                  </label>
                </CardContent>
              </Card>
            </div>
            
            <Card className="bg-orange-600 text-white border-0">
              <CardContent className="p-6">
                <Button className="w-full h-14 text-lg gap-2 bg-white text-orange-700 hover:bg-white/90" onClick={generate} disabled={loading || !form.facts}>
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Generating...</> : <><Send className="h-5 w-5" /> Generate Letter</>}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            <Card className="bg-orange-600 text-white border-0">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold">Demand Letter Generated</h2>
                <p className="text-white/80">${result.demand_amount?.toLocaleString()} • {result.deadline_days} day deadline</p>
                <Button variant="outline" onClick={() => setResult(null)} className="mt-4 text-white border-white/20">New Letter</Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">{result.content}</div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default DemandLetterPage;
